import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import AccessCustomizableOptions from './AccessCustomizableOptions';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import ExpandOptionsButton from './ExpandOptionsButton';
import OfferCard from './OfferCard/OfferCard';
import LoadingWrapper from './LoadingWrapper';
import { getQuoteFormalPricing } from 'api/accessexpress/api';
import {
  LocationType,
  ProductLocationType,
  ProductType,
} from 'screens/SelectServices/schemas';
import ProductSummary from '../BaseComponents/ProductSummary';
import UnavailableServiceBanner from './UnavailableServiceBanner';
import { categorizeOfferings } from '../utils';
import { TermType, groupProducts } from 'screens/SelectServices/utils';
import { useCartContext } from '../CartProvider';
import { observer } from 'mobx-react-lite';
import CurrentLocationHeader from './CurrentLocationHeader';
import { SelectedPackages } from 'context/SingleServiceManagerContext';
import { ProductType as AccessProductType } from 'screens/SelectServices/schemas';
import LeaveProgressModal from './LeaveProgressModal';
import { CategorizedOfferings, ProductCategory } from '../accessExpress.types';
import { ReactComponent as StarlinkLogo } from 'assets/images/starlink.svg';
import { StarfieldGifSimulation } from '../BaseComponents/StarfieldGifSimulation';
import AccessMultiServiceTiles from './AccessMultiServiceTiles/AccessMultiServiceTiles';
import { useAccessCategoryPreloader } from './AccessMultiServiceTiles/useAccessCategoryPreloader';
import { useModal } from 'hooks/useModal';
import ServiceOptimizationModal from './ServiceOptimizationModal';

export const DIA_REFETCH_INTERVAL = 25000;

const AccessProductDetails = observer(() => {
  const navigate = useNavigate();
  const { category: routeCategory } = useParams<{
    category: ProductCategory;
  }>();

  const cart = useCartContext();

  const [sharedHeight, setSharedHeight] = useState<number>(0);

  const handleSharedHeightUpdate = (newHeight: number) => {
    if (!newHeight || newHeight < 100) return;
    setSharedHeight((prev) => Math.max(prev, newHeight));
  };

  useEffect(() => {
    if (!routeCategory) return;

    const validCategories: ProductCategory[] = [
      'Broadband',
      'DIA',
      'Granite MNS',
      'Starlink',
      'All',
    ];
    if (!validCategories.includes(routeCategory as ProductCategory)) return;

    if (routeCategory !== cart.selectedAccessProductCategory) {
      cart.selectAccessProductCategory(routeCategory as ProductCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCategory]);

  const {
    locations,
    goPrevLocation,
    goNextLocation,
    activeLocation,
    locationActiveId,
    loadingDraftForPricing,
    addAccessCartItem,
    removeAccessCartItem,
    isItemAlreadyInCart,
    setLoadingDraftQuote,
    quoteId: cartQuoteId,
    setStartPolling,
    startPolling,
    diaFlowActive,
    setDIAFlowActive,
    addStarlinkCartItem,
    isStarlinkItemAlreadyInCart,
    removeStarlinkCartItem,
    getQuoteIdByCategory,
    showOptimizationModal,
  } = cart;

  const category =
    cart.selectedAccessProductCategory &&
    cart.selectedAccessProductCategory !== 'All'
      ? (cart.selectedAccessProductCategory as ProductCategory)
      : 'Broadband';
  const isAllProducts = routeCategory === 'All';

  const multipleQuoteIds = useAccessCategoryPreloader(
    isAllProducts && !!cart.locations.length,
  );

  const quoteId = isAllProducts ? getQuoteIdByCategory(category) : cartQuoteId;

  const shouldQueryBeEnabled = diaFlowActive
    ? startPolling
    : !loadingDraftForPricing;

  const blocker = useBlocker(() => startPolling || loadingDraftForPricing);

  const { data, isLoading } = useQuery(
    ['access_products', quoteId, category],
    () => getQuoteFormalPricing(quoteId!),
    {
      enabled: !!quoteId && !!locations.length && shouldQueryBeEnabled,
      refetchInterval: (data) => {
        if (category !== 'DIA') return false;
        if (
          data?.quote_status === 'Closed' ||
          data?.quote_status === 'Order services'
        ) {
          return false; // stop polling
        }
        return DIA_REFETCH_INTERVAL; // keep polling otherwise
      },
      retry: false,
      staleTime: DIA_REFETCH_INTERVAL,
      onSettled: (data, error) => {
        if (
          data?.locations?.length ||
          data?.quote_status === 'Closed' ||
          data?.quote_status === 'Order services' ||
          error
        ) {
          setDIAFlowActive(false);
          setStartPolling(false);
          setLoadingDraftQuote(false);
        }
      },
    },
  );

  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [expand, setExpand] = useState(false);
  const [changeLocationTransition, setChangeLocationTransition] =
    useState(false);

  const offerCardsRef = useRef<HTMLDivElement>(null);

  const currentAddress = activeLocation;
  const onExpand = () => setExpand((prev) => !prev);

  const isStarlink = category === 'Starlink';

  // const shouldWaitForNotification = useMemo(() => {
  //   return diaFlowActive && category === 'DIA';
  // }, [category, diaFlowActive]);

  // const onFormalPricingAvailable = useCallback(() => {
  //   setLoadingDraftQuote(false);
  //   setDIAFlowActive(false);
  //   refetchFormalPricing();
  // }, [refetchFormalPricing, setLoadingDraftQuote, setDIAFlowActive]);

  // useEffect(() => {
  //   if (!shouldWaitForNotification) return;
  //   const unsubscribeFunctions = [
  //     notificationDispatcher.on(
  //       DIA_PRICING_AVAILABLE,
  //       onFormalPricingAvailable,
  //     ),
  //   ];
  //   return () => unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
  // }, [
  //   onFormalPricingAvailable,
  //   refetchFormalPricing,
  //   shouldWaitForNotification,
  // ]);

  const currentLocation: ProductLocationType = data?.locations?.find(
    (loc: LocationType & { external_id: string }) =>
      loc.external_id === currentAddress?.external_id,
  );

  const products = useMemo(
    () => groupProducts(data?.locations || []) || [],
    [data?.locations],
  );

  const flattenedProducts = useMemo(() => products.flat(), [products]);

  const locationProducts = useMemo(
    () =>
      flattenedProducts?.find(
        ({ external_id }) => external_id === currentLocation?.external_id,
      ),
    [currentLocation, flattenedProducts],
  );

  const {
    bestOffer,
    bestOfferDetails,
    premiumChoiceDetails,
    premiumChoice,
    lowestPrice,
    lowestPriceDetails,
    sortedOfferings,
    otherOption,
    otherOptionDetails,
  } = categorizeOfferings(
    category as ProductCategory,
    //@ts-expect-error err
    locationProducts as unknown as AccessProductType,
  ) as CategorizedOfferings;

  const modalProps = useModal();

  const handleAddToCart = async (
    tag?: string,
    mns?: SelectedPackages,
    graniteInstall?: boolean,
  ) => {
    const itemMap = {
      'Best offer': bestOffer,
      'Premium choice': premiumChoice,
      'Lowest price': lowestPrice,
      'Other option': otherOption,
    };

    const item = tag ? itemMap[tag as keyof typeof itemMap] : undefined;

    if (item) {
      if (isStarlink) {
        if (
          isStarlinkItemAlreadyInCart({
            ...item,
            bundle: 'enterprise_bundle',
          })
        ) {
          await removeStarlinkCartItem({
            ...item,
            bundle: 'enterprise_bundle',
          });
        } else {
          await addStarlinkCartItem({
            ...(item as unknown as AccessProductType),
            bundle: 'enterprise_bundle',
            graniteInstall: graniteInstall,
          });
        }

        return;
      }

      const offeringID = item?.id;
      if (isItemAlreadyInCart(offeringID!)) {
        removeAccessCartItem(offeringID!);
        return;
      }
      const { success = false }: { success: boolean } = await addAccessCartItem(
        {
          ...(item as unknown as AccessProductType),
          mns_essential: mns?.essential,
          mns_enhanced: mns?.enhanced,
        },
      );
      if (success) {
        setSelectedOffer(tag ?? '');
      }
    }
  };

  const showExpandButton = bestOffer || premiumChoice || lowestPrice;

  const noCuratedOptions =
    currentLocation?.formal_products_offerings.length === 0;

  const slimQEFailed = data?.quote_status === 'Closed';
  const noAvailableProducts = noCuratedOptions || !data || slimQEFailed;

  const shouldShowUnavailableBanner =
    noAvailableProducts && !isLoading && !loadingDraftForPricing;

  const serviceSummaryData = (offering?: ProductType) => {
    return {
      ...offering,
      term: offering?.term,
    } as ProductType & { term: TermType };
  };

  const showLocationTransition = () => {
    setChangeLocationTransition(true);
    setTimeout(() => {
      setChangeLocationTransition(false);
    }, 600);
  };

  const isLastLocation =
    locationActiveId === locations[locations.length - 1]?.id;

  const goToNextLocation = () => {
    setExpand(false);
    if (isLastLocation) {
      navigate('/access-express/v3/order-services/access');
      return;
    }
    goNextLocation();
    showLocationTransition();
  };

  const goToPrevLocation = () => {
    setExpand(false);
    goPrevLocation();
    showLocationTransition();
  };

  const isCheckingAvailability = data?.quote_status === 'Checking availability';

  return (
    <div className="relative my-0 flex min-h-screen w-full flex-col justify-between gap-8 p-4 sm:p-8">
      <ProductSummary
        product={{
          type: 'access',
          title: isStarlink ? <StarlinkLogo className="mt-4" /> : category,
        }}
        isAccessProduct
        isLoading={isLoading || loadingDraftForPricing}
      />
      {shouldShowUnavailableBanner && (
        <UnavailableServiceBanner
          id={activeLocation?.id as string}
          category={category as string}
        />
      )}
      <div className="relative flex w-full flex-1 flex-col gap-6 overflow-visible">
        {activeLocation && (
          <CurrentLocationHeader
            activeLocation={activeLocation}
            type={category as string}
          />
        )}
        <LoadingWrapper
          loading={
            isLoading ||
            loadingDraftForPricing ||
            isCheckingAvailability ||
            !quoteId
          }
          show={true}
        >
          <motion.div
            layout
            transition={{
              height: { duration: 0.75, ease: 'easeInOut' },
            }}
            className={clsx('w-full scrollbar-none', {
              'min-h-[574px]':
                isLoading ||
                loadingDraftForPricing ||
                isCheckingAvailability ||
                !quoteId,
            })}
          >
            <AnimatePresence>
              {!expand && (
                <motion.div
                  key="offer-cards"
                  variants={curatedOptionsVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="w-full"
                >
                  <div
                    ref={offerCardsRef}
                    className="grid w-full grid-cols-1 md:grid-cols-2"
                  >
                    {isStarlink && bestOffer && otherOption && (
                      <StarfieldGifSimulation />
                    )}

                    <div className="md:col-span-2">
                      <OfferCard
                        tag="Best offer"
                        offering={bestOfferDetails}
                        isLoading={
                          isLoading ||
                          loadingDraftForPricing ||
                          changeLocationTransition
                        }
                        selectedOffer={selectedOffer}
                        onAddToCart={handleAddToCart}
                        serviceSummaryData={serviceSummaryData(bestOffer)}
                        category={category}
                        isAllProducts={isAllProducts}
                        collapsed={expand}
                      />
                    </div>
                    {isStarlink && bestOffer && otherOption ? (
                      <div className="md:col-span-2">
                        <OfferCard
                          tag="Other option"
                          offering={otherOptionDetails}
                          isLoading={
                            isLoading ||
                            loadingDraftForPricing ||
                            changeLocationTransition
                          }
                          selectedOffer={selectedOffer}
                          onAddToCart={handleAddToCart}
                          serviceSummaryData={serviceSummaryData(otherOption)}
                          category={category}
                          isAllProducts={isAllProducts}
                        />
                      </div>
                    ) : (
                      <>
                        <OfferCard
                          tag="Premium choice"
                          offering={premiumChoiceDetails}
                          isLoading={
                            isLoading ||
                            loadingDraftForPricing ||
                            changeLocationTransition
                          }
                          selectedOffer={selectedOffer}
                          onAddToCart={handleAddToCart}
                          serviceSummaryData={serviceSummaryData(premiumChoice)}
                          category={category}
                          isAllProducts={isAllProducts}
                          sharedHeight={sharedHeight}
                          onSharedHeightUpdate={handleSharedHeightUpdate}
                        />
                        <OfferCard
                          tag="Lowest price"
                          offering={lowestPriceDetails}
                          isLoading={
                            isLoading ||
                            loadingDraftForPricing ||
                            changeLocationTransition
                          }
                          selectedOffer={selectedOffer}
                          onAddToCart={handleAddToCart}
                          serviceSummaryData={serviceSummaryData(lowestPrice)}
                          category={category}
                          isAllProducts={isAllProducts}
                          sharedHeight={sharedHeight}
                          onSharedHeightUpdate={handleSharedHeightUpdate}
                        />
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="relative">
              {showExpandButton && (
                <ExpandOptionsButton onExpand={onExpand} expand={expand} />
              )}
              <AnimatePresence>
                {expand && (
                  <motion.div
                    key="other-options-content"
                    variants={allOtherOptionsVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="w-full bg-background-base-surface-1"
                  >
                    <AccessCustomizableOptions
                      data={{
                        id: currentLocation?.id,
                        address: currentLocation?.address1,
                        //@ts-expect-error err
                        offerings:
                          sortedOfferings as unknown as AccessProductType,
                        type: sortedOfferings?.[0]?.type,
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </LoadingWrapper>
        {isAllProducts && (
          <div>
            <AccessMultiServiceTiles
              quoteIds={multipleQuoteIds}
              selectedCategory={category}
              onSelectService={(service) => {
                cart.selectAccessProductCategory(service);
              }}
            />
          </div>
        )}
        <motion.div
          layout
          className="mt-2 flex justify-end gap-2"
          transition={{
            height: { duration: 0.75, ease: 'easeInOut' },
          }}
        >
          <GraniteButton
            variant="secondary"
            size="medium"
            onClick={goToPrevLocation}
            disabled={
              locationActiveId === locations[0]?.id || locations?.length <= 1
            }
          >
            Previous location
          </GraniteButton>
          <GraniteButton
            variant="tertiary"
            size="medium"
            onClick={goToNextLocation}
            disabled={locations?.length <= 1}
          >
            {isLastLocation ? 'Finish' : 'Next location'}
          </GraniteButton>
        </motion.div>
      </div>
      <LeaveProgressModal
        isVisible={blocker.state === 'blocked'}
        onCancel={() => {
          blocker.reset?.();
        }}
        onConfirm={() => {
          blocker.proceed?.();
          setLoadingDraftQuote(false);
        }}
      />
      <ServiceOptimizationModal
        {...modalProps}
        isOpen={showOptimizationModal}
      />
    </div>
  );
});

const allOtherOptionsVariants = {
  initial: {
    height: 0,
  },
  animate: {
    height: 'auto',
    transition: { duration: 0.75, ease: 'easeInOut' },
  },
  exit: {
    height: 0,
    transition: { delay: 0.5, duration: 0.75, ease: 'easeInOut' },
  },
};

const curatedOptionsVariants = {
  initial: {
    height: 0,
    opacity: 0,
  },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: { delay: 0.5, duration: 0.75, ease: 'easeInOut' },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.75, ease: 'easeInOut' },
  },
};

export default AccessProductDetails;
